import {
  Box,
  TextField,
  Autocomplete,
  createFilterOptions,
  MenuItem,
  Button,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { VendorDTO } from "../../types/VendorDTO";
import { useEffect, useRef } from "react";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type HeaderForm = {
  vendor: VendorDTO | null;
  incomingDocNo: string;
  dueDate: string;
  documentDate: string;
  salesDate: string;
  currencyCode: string;
  attachment: File | null;
};

type Props = {
  vendors: VendorDTO[];
  currencies: string[];
  currenciesLoading?: boolean;
  value: HeaderForm;
  onChange: (value: HeaderForm) => void;
};

export default function AddInvoiceForm({
  vendors,
  currencies,
  currenciesLoading = false,
  value,
  onChange,
}: Props) {
  const { t } = useAppTranslation("common");

  const vendorRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    vendorRef.current?.focus();
  }, []);

  const filterOptions = createFilterOptions<VendorDTO>({
    stringify: (option) => {
      const nip = option.vatRegistrationNo || "";
      return `${option.name} ${option.no} ${nip}`;
    },
  });

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
        <DescriptionIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        <Typography variant="subtitle2" color="text.secondary">
          {t("Dane dokumentu")}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Autocomplete
          options={vendors}
          value={value.vendor}
          onChange={(_, newValue) => onChange({ ...value, vendor: newValue })}
          filterOptions={filterOptions}
          getOptionLabel={(v) => {
            const nip = v.vatRegistrationNo
              ? ` | NIP: ${v.vatRegistrationNo}`
              : "";
            return `${v.name} (${v.no})${nip}`;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={vendorRef}
              label={t("Dostawca")}
              error={!value.vendor}
              required
              size="small"
            />
          )}
        />

        <TextField
          label={t("Numer faktury")}
          required
          size="small"
          error={value.incomingDocNo.trim() === ""}
          value={value.incomingDocNo}
          onChange={(e) =>
            onChange({
              ...value,
              incomingDocNo: e.target.value,
            })
          }
        />

        <TextField
          label={t("Termin płatności")}
          type="date"
          required
          size="small"
          error={value.dueDate === ""}
          InputLabelProps={{ shrink: true }}
          value={value.dueDate}
          onChange={(e) =>
            onChange({
              ...value,
              dueDate: e.target.value,
            })
          }
        />

        <TextField
          label={t("Data dokumentu")}
          type="date"
          required
          size="small"
          error={value.documentDate === ""}
          InputLabelProps={{ shrink: true }}
          value={value.documentDate}
          onChange={(e) =>
            onChange({
              ...value,
              documentDate: e.target.value,
            })
          }
        />

        <TextField
          label={t("Data sprzedaży")}
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={value.salesDate}
          onChange={(e) =>
            onChange({
              ...value,
              salesDate: e.target.value,
            })
          }
        />

        <TextField
          select
          label={t("Waluta")}
          required
          size="small"
          error={value.currencyCode === ""}
          value={value.currencyCode}
          onChange={(e) =>
            onChange({
              ...value,
              currencyCode: e.target.value,
            })
          }
          disabled={currenciesLoading}
        >
          {currencies.map((currency) => (
            <MenuItem key={currency} value={currency}>
              {currency}
            </MenuItem>
          ))}
        </TextField>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            minHeight: 40,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            component="label"
            size="small"
            startIcon={<AttachFileIcon />}
          >
            {t("Dodaj załącznik")}
            <input
              hidden
              type="file"
              onChange={(e) =>
                onChange({
                  ...value,
                  attachment: e.target.files?.[0] || null,
                })
              }
            />
          </Button>

          {value.attachment && (
            <Chip
              label={value.attachment.name}
              size="small"
              variant="outlined"
              onDelete={() => onChange({ ...value, attachment: null })}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}